import { useState, useEffect, useRef } from 'react';
import getCategories from '../../services/getCategories';
import Suggestions from '../suggestions/suggestions';
import CategorySelector from './categorySelector';

// Constantes de configuración
const MIN_SEARCH_LENGTH = 3;
const INPUT_STYLES = {
  fontSize: '16px',
  WebkitOverflowScrolling: 'touch',
  overflowX: 'auto',
  whiteSpace: 'normal',
  textOverflow: 'clip'
};

const KEYBOARD_KEYS = {
  ENTER: 'Enter',
  BACKSPACE: 'Backspace'
};

/**
 * Componente de formulario de búsqueda con autocompletado y filtros
 * 
 * @description Renderiza un formulario completo de búsqueda que incluye:
 * - Input de texto con autocompletado y sugerencias
 * - Selector de categorías con radio buttons
 * - Botones de búsqueda y reset
 * - Manejo de eventos de teclado (Enter, Backspace)
 * - Sincronización bidireccional con componente padre
 * - Soporte responsive para dispositivos móviles
 */
export default function SearchForm({
  query,
  category,
  onQueryChange,
  onCategoryChange,
  onReset,
  setQuery,
  isMobile,
}) {
  // Estados locales
  const [text, setText] = useState(query);
  const [textNew, setTextNew] = useState();
  const [categories, setCategories] = useState();
  const [showSugerencias, setShowSugerencias] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);
  const [listaSugerencias, setListaSugerencias] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const categoriesLoaded = useRef(false);

  /**
   * Carga las categorías disponibles desde el servicio al montar el componente
   * 
   * @description Obtiene la lista de categorías para el selector usando getCategories.
   * Utiliza una ref (categoriesLoaded) para evitar múltiples llamadas al servicio.
   * En caso de error, resetea la ref para permitir reintentos. Se ejecuta solo
   * una vez al montar el componente.
   */
  useEffect(() => {
    if (categoriesLoaded.current) return;

    categoriesLoaded.current = true;
    getCategories()
      .then((data) => {
        setCategories(data);
      })
      .catch((error) => {
        console.error("Error al obtener las categorias.", error);
        categoriesLoaded.current = false; // Permitir reintento en caso de error
      });
  }, []);

  /**
   * Sincroniza el estado local del texto con la prop query del componente padre
   * 
   * @description Actualiza el estado text cuando la prop query cambia desde
   * el componente padre. Esto mantiene sincronizado el input local con el
   * estado global de búsqueda. Se ejecuta cada vez que query cambia.
   */
  useEffect(() => {
    if (query !== text) {
      setText(query);
    }
  }, [query])

  /**
   * Controla la visibilidad de sugerencias basado en la longitud del texto
   * 
   * @description Actualiza el estado showSugerencias cuando el texto cambia.
   * Las sugerencias se habilitan solo cuando el texto tiene al menos 3 caracteres
   * (MIN_SEARCH_LENGTH). Se ejecuta cada vez que el estado text cambia.
   */
  useEffect(() => {
    setShowSugerencias(text.length >= MIN_SEARCH_LENGTH);
  }, [text])

  /**
   * Configura listeners para detectar el estado de focus del input
   * 
   * @description Registra event listeners para los eventos focus y blur
   * del input de búsqueda para actualizar el estado hasFocus. Este estado
   * es crucial para determinar cuándo mostrar las sugerencias. Se ejecuta
   * solo al montar el componente y limpia los listeners al desmontarlo.
   */
  useEffect(() => {
    const inputElement = inputRef.current;
    if (!inputElement) return;

    const handleFocus = () => setHasFocus(true);
    const handleBlur = () => setHasFocus(false);

    inputElement.addEventListener('focus', handleFocus);
    inputElement.addEventListener('blur', handleBlur);

    // Limpiar eventos al desmontar el componente
    return () => {
      inputElement.removeEventListener('focus', handleFocus);
      inputElement.removeEventListener('blur', handleBlur);
    };
  }, []);

  /**
   * Maneja los cambios de texto en el input de búsqueda
   * 
   * @description Actualiza el estado local del texto cuando el usuario
   * escribe en el input. Si el campo se vacía completamente, ejecuta
   * handleReset para limpiar todos los estados relacionados.
   */
  const handleQueryChange = (event) => {
    const value = event.target.value;
    setText(value);

    if (value === '') {
      handleReset();
    }
  };

  /**
   * Maneja eventos de teclado en el input de búsqueda
   * 
   * @description Gestiona las teclas Enter y Backspace:
   * - Enter: Ejecuta búsqueda solo si no hay sugerencia seleccionada,
   *   usando textNew si existe o text como fallback
   * - Backspace: Resetea la selección de sugerencias y limpia textNew
   */
  const handleKeyDown = (event) => {
    const key = event.key;

    // Manejar Enter para búsqueda cuando no hay sugerencia seleccionada
    if (key === KEYBOARD_KEYS.ENTER && selectedIndex === -1) {
      event.preventDefault();
      const queryToUse = textNew?.trim().length > 0 ? textNew : text;
      onQueryChange(queryToUse);
      setShowSugerencias(false);
    }

    if (key === KEYBOARD_KEYS.BACKSPACE) {
      setSelectedIndex(-1);
      setTextNew('');
    }
  };

  /**
   * Ejecuta la búsqueda al hacer clic en el botón de búsqueda
   * 
   * @description Dispara la búsqueda usando el texto actual del input
   * y oculta las sugerencias. Se ejecuta cuando el usuario hace clic
   * en el botón de búsqueda (ícono de lupa) en lugar de usar Enter.
   */
  const handleSearch = () => {
    onQueryChange(text);
    setShowSugerencias(false);
  }

  /**
   * Reinicia completamente el formulario de búsqueda
   * 
   * @description Limpia todos los estados relacionados con la búsqueda:
   * texto del input, sugerencias, selección activa y llama al callback
   * onReset del componente padre para sincronizar el estado global.
   * Se ejecuta al hacer clic en el botón reset o cuando el input se vacía.
   */
  const handleReset = () => {
    setText('');
    setTextNew('');
    setListaSugerencias([]);
    setSelectedIndex(-1);
    if (onReset) {
      onReset();
    }
  }

  /**
   * Determina si se deben mostrar las sugerencias de búsqueda
   * 
   * @description Evalúa tres condiciones necesarias para mostrar sugerencias:
   * 1. El texto tiene al menos 3 caracteres (MIN_SEARCH_LENGTH)
   * 2. El estado showSugerencias está activo
   * 3. El input tiene focus actualmente
   * Solo cuando todas las condiciones se cumplen se renderiza el componente Suggestions.
   */
  const shouldShowSuggestions = text?.trim().length >= MIN_SEARCH_LENGTH && showSugerencias && hasFocus;

  return (
    <>
      <div className="search-container">
        <input
          type="text"
          id="query"
          value={text}
          onChange={handleQueryChange}
          onKeyDown={handleKeyDown}
          placeholder="Buscar..."
          className="search-input"
          autoComplete='off'
          ref={inputRef}
          style={INPUT_STYLES}
        />

        <button id="buscarBtn" onClick={handleSearch} className="search-button" aria-label="Buscar" title="Buscar">
          <span className="item-search-unotv" aria-hidden="true"></span>
        </button>

        {/* Botón para resetear la selección */}
        <button
          type="button"
          className="reset-button"
          onClick={handleReset}
          aria-label="Limpiar búsqueda"
          title="Limpiar búsqueda"
          id="resetBtn"
        />

        {/* Muestra sugerencias si aplica */}
        {shouldShowSuggestions && (
          <Suggestions
            palabra={text}
            setText={setText}
            setTextNew={setTextNew}
            setShowSugerencias={setShowSugerencias}
            inputRef={inputRef}
            hasFocus={hasFocus}
            setQuery={setQuery}
            category={category}
            isMobile={isMobile}
            onQueryChange={onQueryChange}
            selectedIndex={selectedIndex}
            setSelectedIndex={setSelectedIndex}
          />
        )}
      </div>

      <CategorySelector categories={categories} category={category} onCategoryChange={onCategoryChange} />
    </>
  );
};